import { useCallback } from "react";
import { Component, CreateComponentRequest } from "../types/component";
import { SubAssembly } from "../types/subAssembly";
import { useApiService } from "./apiService";

export const useComponentService = () => {
  const apiService = useApiService();

  

  const getComponents = useCallback(
    async (filter?: { type?: string; attachment?: "attached" | "nonAttached" }) => {
      const params = new URLSearchParams();
      if (filter?.type && filter.type !== "All") {
        params.set("type", filter.type);
      }
      if (filter?.attachment) {
        params.set("attachment", filter.attachment);
      }
      const qs = params.toString();
      const endpoint = qs.length > 0 ? `/api/components?${qs}` : "/api/components";

      const response = await apiService.apiCall<Component[]>({
        method: "GET",
        endpoint,
        requireAuth: true,
      });

      return response;
    },
    [apiService]
  );


  const getComponent = useCallback(
    async (id: string) => {
      const response = await apiService.apiCall<Component>({
        method: "GET",
        endpoint: `/api/components/${id}`,
        requireAuth: true,
      });

      return response;
    },
    [apiService]
  );



const createComponent = useCallback(
  async (component: CreateComponentRequest) => {
    const response = await apiService.apiCall<Component>({
      method: "POST",
      endpoint: "/api/components",
      payload: component,
      requireAuth: true,
    });

    return response;
  },
  [apiService]
);


  const updateComponent = useCallback(
    async (id: string, component: Partial<Component>) => {
      const response = await apiService.apiCall<Component>({
        method: "PUT",
        endpoint: `/api/components/${id}`,
        payload: component,
        requireAuth: true,
      });

      return response;
    },
    [apiService]
  );


  const deleteComponent = useCallback(
    async (id: string) => {
      const response = await apiService.apiCall<void>({
        method: "DELETE",
        endpoint: `/api/components/${id}`,
        requireAuth: true,
      });

      return response;
    },
    [apiService]
  );



  const attachComponentToAirframe = useCallback(
    async (
      componentId: string,
      tailNumber: string,
      location?: string
    ) => {
      const payload = {
        componentId,
        tailNumber,
        location,
      };

      const response = await apiService.apiCall<Component>({
        method: "POST",
        endpoint: `/api/components/${componentId}/attach`,
        payload,
        requireAuth: true,
      });

      return response;
    },
    [apiService]
  );


  const detachComponentFromAirframe = useCallback(
    async (componentId: string) => {
      const response = await apiService.apiCall<Component>({
        method: "POST",
        endpoint: `/api/components/${componentId}/detach`,
        requireAuth: true,
      });

      return response;
    },
    [apiService]
  );

  const getComponentSubAssemblies = useCallback(
    async (componentId: string) => {
      const response = await apiService.apiCall<any[]>({
        method: "GET",
        endpoint: `/api/components/${componentId}/sub-assemblies`,
        requireAuth: true,
      });

      const data = Array.isArray(response) ? response : response?.data;
      if (!Array.isArray(data)) return [];

      return data.map(
        (item): SubAssembly => ({
          id: item.id ?? item.Id,
          landingGearType: (item.landingGearType ?? item.LandingGearType ?? "").toUpperCase(),
          subAssemblyType: item.subAssemblyType ?? item.SubAssemblyType ?? "",
          serialNumber: item.serialNumber ?? item.SerialNumber ?? "",
          partNumber: item.partNumber ?? item.PartNumber ?? null,
          installDate: item.installDate ?? item.InstallDate ?? null,
          componentId: item.componentId ?? item.ComponentId ?? null,
          status: item.status ?? item.Status ?? "",
          attachedToAirframe: item.attachedToAirframe ?? item.AttachedToAirframe ?? null,
          attachmentLocation: item.attachmentLocation ?? item.AttachmentLocation ?? null,
          createdDate: item.createdDate ?? item.CreatedDate,
          modifiedDate: item.modifiedDate ?? item.ModifiedDate ?? null,
          createdBy: item.createdBy ?? item.CreatedBy ?? null,
          editedBy: item.editedBy ?? item.EditedBy ?? null,
        })
      );
    },
    [apiService]
  );

  const syncComponentSubAssemblies = useCallback(
    async (componentId: string, subAssemblyIds: string[]) => {
      await apiService.apiCall<void>({
        method: "PUT",
        endpoint: `/api/components/${componentId}/sub-assemblies`,
        payload: { subAssemblyIds },
        requireAuth: true,
      });
    },
    [apiService]
  );

  const detachSubAssemblyFromComponent = useCallback(
    async (
      componentId: string,
      subAssemblyId: string,
      payload: { offWingType: string; shopVisitReasonId?: string | null }
    ) => {
      await apiService.apiCall<void>({
        method: "POST",
        endpoint: `/api/components/${componentId}/sub-assemblies/${subAssemblyId}/detach`,
        payload: {
          offWingType: payload.offWingType,
          shopVisitReasonId: payload.shopVisitReasonId || null,
        },
        requireAuth: true,
      });
    },
    [apiService]
  );

  return {
    getComponents,
    getComponent,
    createComponent,
    updateComponent,
    deleteComponent,
    attachComponentToAirframe,
    detachComponentFromAirframe,
    getComponentSubAssemblies,
    syncComponentSubAssemblies,
    detachSubAssemblyFromComponent,
  };
};